import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import type { CountryCode } from 'libphonenumber-js';

import { CheckinPageShell } from '@/components/checkin-page-shell';
import { DEFAULT_COUNTRY, GuestPhoneField } from '@/components/guest-phone-field';
import { PoweredByFooter } from '@/components/powered-by-footer';
import { api, ApiError } from '@/lib/api';
import {
  GeolocationDeniedError,
  GeolocationUnavailableError,
  readDevicePosition,
} from '@/lib/device-location';
import { readGatewayAuth, writeDisplayToken } from '@/lib/gateway-auth';

interface KioskRuntimeState {
  display: {
    token: string;
    expiresAt: string;
    rotationSeconds: number;
  } | null;
}

const isDisplayTokenError = (error: ApiError): boolean =>
  error.code === 'GATEWAY_TOKEN_EXPIRED' || error.code === 'GATEWAY_TOKEN_INVALID';

const fetchFreshDisplayToken = async (
  pairingCode: string,
  accessToken: string,
): Promise<string | null> => {
  const position = await readDevicePosition();
  const state = await api.get<KioskRuntimeState>(
    '/gateway/attendance/kiosk-state',
    accessToken,
    position,
  );
  const token = state.display?.token ?? null;
  if (token) {
    writeDisplayToken(pairingCode, token);
  }
  return token;
};

const resolveDisplayTokenForSubmit = async (
  pairingCode: string,
  accessToken: string,
): Promise<string | null> => fetchFreshDisplayToken(pairingCode, accessToken);

export const GuestFormScreen = () => {
  const { t } = useTranslation();
  const { pairingCode = '' } = useParams();
  const auth = useMemo(() => readGatewayAuth(pairingCode), [pairingCode]);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState<string | null>(null);
  const [phoneCountry, setPhoneCountry] = useState<CountryCode>(DEFAULT_COUNTRY);
  const [email, setEmail] = useState('');
  const [wantsFollowUp, setWantsFollowUp] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [phoneInvalid, setPhoneInvalid] = useState(false);
  const [done, setDone] = useState(false);

  const submitGuestCheckIn = async (displayToken: string) => {
    await api.post(
      '/gateway/attendance/guest-check-ins',
      {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone!,
        phoneCountry,
        email: email.trim() || null,
        wantsFollowUp,
      },
      displayToken,
    );
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!auth) return;

    if (!phone) {
      setPhoneInvalid(true);
      setErrorCode(null);
      return;
    }

    setSubmitting(true);
    setErrorCode(null);
    setPhoneInvalid(false);
    try {
      let displayToken = await resolveDisplayTokenForSubmit(pairingCode, auth.accessToken);
      if (!displayToken) {
        setErrorCode('ATTENDANCE_SESSION_NOT_OPEN');
        return;
      }

      try {
        await submitGuestCheckIn(displayToken);
      } catch (error) {
        if (!(error instanceof ApiError) || !isDisplayTokenError(error)) {
          throw error;
        }
        displayToken = await fetchFreshDisplayToken(pairingCode, auth.accessToken);
        if (!displayToken) {
          setErrorCode('ATTENDANCE_SESSION_NOT_OPEN');
          return;
        }
        await submitGuestCheckIn(displayToken);
      }

      setDone(true);
    } catch (error) {
      if (error instanceof GeolocationDeniedError) {
        setErrorCode('GEOLOCATION_DENIED');
      } else if (error instanceof GeolocationUnavailableError) {
        setErrorCode('GEOLOCATION_UNAVAILABLE');
      } else if (error instanceof ApiError) {
        setErrorCode(error.code ?? 'UNEXPECTED');
      } else {
        setErrorCode('UNEXPECTED');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!auth) {
    return (
      <CheckinPageShell footer={<PoweredByFooter />}>
        <section className="checkin-card checkin-card--centered">
          <p className="checkin-card-note">{t('guest.unavailable')}</p>
          <Link to={`/pair/${encodeURIComponent(pairingCode)}`} className="checkin-text-link">
            {t('guest.backToKiosk')}
          </Link>
        </section>
      </CheckinPageShell>
    );
  }

  return (
    <CheckinPageShell footer={<PoweredByFooter />}>
      <section className="checkin-card checkin-card--guest">
        <header className="checkin-guest-header">
          <h1 className="church-brand-title">{t('guest.title')}</h1>
          <p className="church-brand-description">{t('guest.lead')}</p>
        </header>

        {done ? (
          <div className="checkin-guest-success">
            <p>{t('guest.success')}</p>
            <Link to={`/display/${encodeURIComponent(pairingCode)}`} className="checkin-text-link">
              {t('guest.backToKiosk')}
            </Link>
          </div>
        ) : (
          <form className="checkin-guest-form" onSubmit={(event) => void submit(event)}>
            <label className="checkin-field">
              <span>{t('guest.firstName')}</span>
              <input
                required
                autoComplete="given-name"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
              />
            </label>
            <label className="checkin-field">
              <span>{t('guest.lastName')}</span>
              <input
                required
                autoComplete="family-name"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
              />
            </label>
            <GuestPhoneField
              value={phone}
              country={phoneCountry}
              onChange={setPhone}
              onCountryChange={setPhoneCountry}
              disabled={submitting}
            />
            {phoneInvalid ? <p className="checkin-error">{t('guest.phoneInvalid')}</p> : null}
            <label className="checkin-field">
              <span>{t('guest.email')}</span>
              <input
                type="email"
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <label className="checkin-checkbox">
              <input
                type="checkbox"
                checked={wantsFollowUp}
                onChange={(event) => setWantsFollowUp(event.target.checked)}
              />
              <span>{t('guest.followUp')}</span>
            </label>

            {errorCode ? <p className="checkin-error">{t(`errors.${errorCode}`, t('error'))}</p> : null}

            <div className="checkin-form-actions">
              <Link to={`/display/${encodeURIComponent(pairingCode)}`} className="checkin-secondary-link">
                {t('guest.cancel')}
              </Link>
              <button type="submit" className="checkin-primary-button" disabled={submitting}>
                {t('guest.submit')}
              </button>
            </div>
          </form>
        )}
      </section>
    </CheckinPageShell>
  );
};
